import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, AlertTriangle, Pill, Thermometer, Coffee, 
  ArrowRight, ArrowLeft, Check, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

export default function Intake() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState({
    age_range: 'adult',
    pregnancy: false,
    breastfeeding: false,
    hypertension: false,
    diabetes: false,
    reflux_ulcer: false,
    gallstones: false,
    kidney_disease: false,
    allergies: [],
    anticoagulants: false,
    ssri_snri: false,
    maoi: false,
    sedatives: false,
    stimulants: false,
    thyroid_meds: false,
    caffeine_sensitivity: false,
    heat_signs: false,
    cold_signs: false,
    preferred_preparation: 'infuzie',
    intensity: 'blând'
  });

  useEffect(() => {
    // Load saved profile
    const savedProfile = localStorage.getItem('acupedia_profile');
    if (savedProfile) {
      setProfile(JSON.parse(savedProfile));
    }
    
    // Check if user has kit
    const kit = localStorage.getItem('acupedia_kit');
    if (!kit || JSON.parse(kit).length === 0) {
      toast.error('Te rugăm să configurezi mai întâi trusa de plante');
      navigate('/kit');
    }
  }, [navigate]);

  const updateProfile = (key, value) => {
    setProfile(prev => {
      const updated = { ...prev, [key]: value };
      localStorage.setItem('acupedia_profile', JSON.stringify(updated));
      return updated;
    });
  };

  const handleSubmit = () => {
    // Validate severe conditions
    if (profile.age_range === 'child') {
      toast.warning('Pentru copii, consultați un medic înainte de a folosi ceaiuri terapeutice.');
    }
    
    localStorage.setItem('acupedia_profile', JSON.stringify(profile));
    toast.success('Profil salvat cu succes!');
    navigate('/results');
  };

  const steps = [
    { num: 1, title: 'Date personale' },
    { num: 2, title: 'Condiții medicale' },
    { num: 3, title: 'Medicamente' },
    { num: 4, title: 'Preferințe' }
  ];

  return (
    <div className="py-8 md:py-12 animate-fade-in">
      <div className="container-acupedia max-w-3xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-acupedia-teal flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <h1 className="section-header mb-0">Profilul Tău</h1>
          </div>
          <p className="section-subheader mx-auto">
            Completează profilul pentru a primi rețete sigure și personalizate
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {steps.map((s, index) => (
            <div key={s.num} className="flex items-center">
              <button
                onClick={() => setStep(s.num)}
                className={`progress-step ${
                  step === s.num ? 'active' : step > s.num ? 'completed' : 'pending'
                }`}
                data-testid={`step-${s.num}-btn`}
              >
                {step > s.num ? <Check className="w-4 h-4" /> : s.num}
              </button>
              {index < steps.length - 1 && (
                <div className={`w-8 sm:w-16 h-0.5 mx-1 ${
                  step > s.num ? 'bg-acupedia-gold' : 'bg-stone-200'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Form Card */}
        <div className="card-acupedia p-6 md:p-8">
          {/* Step 1: Personal Data */}
          {step === 1 && (
            <div className="space-y-6 animate-fade-in">
              <h2 className="font-heading text-xl text-stone-800 mb-6">Date personale</h2>
              
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-3 block">
                  Categoria de vârstă
                </Label>
                <RadioGroup
                  value={profile.age_range}
                  onValueChange={(value) => updateProfile('age_range', value)}
                  className="flex flex-wrap gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="adult" id="adult" data-testid="age-adult" />
                    <Label htmlFor="adult" className="cursor-pointer">Adult (18+)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="teen" id="teen" data-testid="age-teen" />
                    <Label htmlFor="teen" className="cursor-pointer">Adolescent (12-18)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="child" id="child" data-testid="age-child" />
                    <Label htmlFor="child" className="cursor-pointer">Copil (&lt;12)</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3 p-4 bg-stone-50 rounded-lg">
                  <Checkbox
                    id="pregnancy"
                    checked={profile.pregnancy}
                    onCheckedChange={(checked) => updateProfile('pregnancy', checked)}
                    data-testid="pregnancy-checkbox"
                  />
                  <Label htmlFor="pregnancy" className="cursor-pointer">
                    Sunt însărcinată
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-stone-50 rounded-lg">
                  <Checkbox
                    id="breastfeeding"
                    checked={profile.breastfeeding}
                    onCheckedChange={(checked) => updateProfile('breastfeeding', checked)}
                    data-testid="breastfeeding-checkbox"
                  />
                  <Label htmlFor="breastfeeding" className="cursor-pointer">
                    Alăptez
                  </Label>
                </div>
              </div>

              {(profile.pregnancy || profile.breastfeeding) && (
                <div className="medical-disclaimer">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">
                      <strong>Atenție:</strong> În sarcină și în timpul alăptării, multe plante sunt contraindicate. 
                      Vom genera doar rețete considerate sigure, dar consultați întotdeauna medicul.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Medical Conditions */}
          {step === 2 && (
            <div className="space-y-6 animate-fade-in">
              <h2 className="font-heading text-xl text-stone-800 mb-6">Condiții medicale</h2>
              <p className="text-sm text-stone-600 mb-4">
                Selectează condițiile care ți se aplică pentru a evita plantele contraindicate:
              </p>
              
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { key: 'hypertension', label: 'Hipertensiune arterială' },
                  { key: 'diabetes', label: 'Diabet zaharat' },
                  { key: 'reflux_ulcer', label: 'Reflux gastric / Ulcer' },
                  { key: 'gallstones', label: 'Calculi biliari' },
                  { key: 'kidney_disease', label: 'Afecțiuni renale' },
                ].map((condition) => (
                  <div key={condition.key} className="flex items-center space-x-3 p-4 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                    <Checkbox
                      id={condition.key}
                      checked={profile[condition.key]}
                      onCheckedChange={(checked) => updateProfile(condition.key, checked)}
                      data-testid={`condition-${condition.key}`}
                    />
                    <Label htmlFor={condition.key} className="cursor-pointer flex-1">
                      {condition.label}
                    </Label>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-cyan-800">
                    Aceste informații ne ajută să excludem plantele care pot interacționa 
                    negativ cu starea ta de sănătate.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Medications */}
          {step === 3 && (
            <div className="space-y-6 animate-fade-in">
              <h2 className="font-heading text-xl text-stone-800 mb-6">
                <Pill className="inline w-6 h-6 mr-2 text-acupedia-teal" />
                Medicamente
              </h2>
              <p className="text-sm text-stone-600 mb-4">
                Selectează clasele de medicamente pe care le iei:
              </p>
              
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { key: 'anticoagulants', label: 'Anticoagulante / Antiagregante', desc: '(Aspenter, Warfarină, etc.)' },
                  { key: 'ssri_snri', label: 'Antidepresive SSRI/SNRI', desc: '(Sertraline, Venlafaxine, etc.)' },
                  { key: 'maoi', label: 'Inhibitori MAO', desc: '(Moclobemidă, etc.)' },
                  { key: 'sedatives', label: 'Sedative / Anxiolitice', desc: '(Diazepam, Alprazolam, etc.)' },
                  { key: 'stimulants', label: 'Stimulente', desc: '(Metilfenidat, etc.)' },
                  { key: 'thyroid_meds', label: 'Medicamente tiroidiene', desc: '(Euthyrox, etc.)' },
                ].map((med) => (
                  <div key={med.key} className="flex items-start space-x-3 p-4 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                    <Checkbox
                      id={med.key}
                      checked={profile[med.key]}
                      onCheckedChange={(checked) => updateProfile(med.key, checked)}
                      className="mt-0.5"
                      data-testid={`med-${med.key}`}
                    />
                    <div>
                      <Label htmlFor={med.key} className="cursor-pointer font-medium">
                        {med.label}
                      </Label>
                      <p className="text-xs text-stone-500">{med.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {(profile.anticoagulants || profile.ssri_snri || profile.maoi) && (
                <div className="medical-disclaimer">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">
                      <strong>Atenție:</strong> Unele plante pot interacționa cu medicamentele selectate. 
                      Vom exclude automat plantele cu risc de interacțiune.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Preferences */}
          {step === 4 && (
            <div className="space-y-6 animate-fade-in">
              <h2 className="font-heading text-xl text-stone-800 mb-6">Preferințe</h2>
              
              {/* Sensitivities */}
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-3 block">
                  Sensibilități
                </Label>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="flex items-center space-x-3 p-4 bg-stone-50 rounded-lg">
                    <Checkbox
                      id="caffeine_sensitivity"
                      checked={profile.caffeine_sensitivity}
                      onCheckedChange={(checked) => updateProfile('caffeine_sensitivity', checked)}
                      data-testid="caffeine-sensitivity"
                    />
                    <Label htmlFor="caffeine_sensitivity" className="cursor-pointer flex items-center gap-2">
                      <Coffee className="w-4 h-4 text-stone-500" />
                      Sensibilitate la cofeină
                    </Label>
                  </div>
                </div>
              </div>

              {/* Energetic Signs */}
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-3 block">
                  Semne energetice (opțional - ajută la personalizare)
                </Label>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="flex items-start space-x-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <Checkbox
                      id="heat_signs"
                      checked={profile.heat_signs}
                      onCheckedChange={(checked) => updateProfile('heat_signs', checked)}
                      className="mt-0.5"
                      data-testid="heat-signs"
                    />
                    <div>
                      <Label htmlFor="heat_signs" className="cursor-pointer font-medium text-orange-800">
                        Semne de căldură
                      </Label>
                      <p className="text-xs text-orange-700 mt-1">
                        Agitație, transpirații nocturne, sete excesivă, față roșie
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                    <Checkbox
                      id="cold_signs"
                      checked={profile.cold_signs}
                      onCheckedChange={(checked) => updateProfile('cold_signs', checked)}
                      className="mt-0.5"
                      data-testid="cold-signs"
                    />
                    <div>
                      <Label htmlFor="cold_signs" className="cursor-pointer font-medium text-cyan-800">
                        Semne de frig
                      </Label>
                      <p className="text-xs text-cyan-700 mt-1">
                        Extremități reci, oboseală, față palidă, digestie lentă
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preparation Method */}
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-3 block">
                  Metoda de preparare preferată
                </Label>
                <RadioGroup
                  value={profile.preferred_preparation}
                  onValueChange={(value) => updateProfile('preferred_preparation', value)}
                  className="grid sm:grid-cols-2 gap-3"
                >
                  {[
                    { value: 'infuzie', label: 'Infuzie clasică', desc: 'Se toarnă apă fierbinte peste plante' },
                    { value: 'decoct', label: 'Decoct', desc: 'Se fierb plantele 10-15 min' },
                    { value: 'infuzie_rece', label: 'Infuzie la rece', desc: '8-12 ore în apă rece' },
                    { value: 'rapid', label: 'Metodă rapidă', desc: 'Preparare în 3-5 minute' },
                  ].map((method) => (
                    <div key={method.value} className="flex items-start space-x-3 p-4 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                      <RadioGroupItem value={method.value} id={method.value} className="mt-0.5" data-testid={`prep-${method.value}`} />
                      <div>
                        <Label htmlFor={method.value} className="cursor-pointer font-medium">
                          {method.label}
                        </Label>
                        <p className="text-xs text-stone-500">{method.desc}</p>
                      </div>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Intensity */}
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-3 block">
                  Intensitatea formulei
                </Label>
                <RadioGroup
                  value={profile.intensity}
                  onValueChange={(value) => updateProfile('intensity', value)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="blând" id="bland" data-testid="intensity-bland" />
                    <Label htmlFor="bland" className="cursor-pointer">Blând</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="mediu" id="mediu" data-testid="intensity-mediu" />
                    <Label htmlFor="mediu" className="cursor-pointer">Mediu</Label>
                  </div>
                </RadioGroup>
                <p className="text-xs text-stone-500 mt-2">
                  Formula blândă folosește cantități mai mici, ideală pentru începători.
                </p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-stone-200">
            <Button
              variant="outline"
              onClick={() => step > 1 ? setStep(step - 1) : navigate('/kit')}
              className="btn-ghost"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {step > 1 ? 'Înapoi' : 'La trusă'}
            </Button>
            
            {step < 4 ? (
              <Button
                onClick={() => setStep(step + 1)}
                className="btn-primary"
                data-testid="next-btn"
              >
                Continuă
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                className="btn-primary"
                data-testid="generate-recipes-btn"
              >
                Generează Rețete
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>

        {/* Medical Disclaimer */}
        <div className="medical-disclaimer mt-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-acupedia-gold flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm">
                <strong>Disclaimer Medical:</strong> Acest conținut are scop educativ și nu înlocuiește consultul medical. 
                Dacă simptomele persistă peste 72h, aveți febră, dificultăți de respirație, sânge sau scădere în greutate, 
                consultați imediat un medic.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
